import algoliasearch from "algoliasearch";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  type GlobalHit,
  type PersonHit,
  type FacilityHit,
  type SectionHit,
  createGlobalSearchUiState,
  getGlobalHitListItemContent,
  filterGlobalSearchHits
} from "@/components/AlgoliaSearch";
import { collectFilteredHits } from "@/components/AlgoliaSearch/collectFilteredHits";
import AppLayout from "@/layouts/AppLayout";
import PageHeading from "@/components/PageHeading";
import Chip, { ChipSet } from "@/components/Chip";
import Divider from "@/components/Divider";
import EmptyState from "@/components/EmptyState";
import IconButton from "@/components/IconButton";
import ListItem from "@/components/ListItem/ListItem";
import { normalizePhoneSearchQuery } from "@/lib/phoneHelpers";
import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate, Path } from "@/router";

const appId = import.meta.env.VITE_ALGOLIA_APP_ID;
const apiKey = import.meta.env.VITE_ALGOLIA_API_KEY;
const HITS_PER_PAGE = 20;

type ObjectTypeFilter = "all" | "Person" | "Facility" | "Section";

type TypeCounts = { Person: number; Facility: number; Section: number };

function SearchResultCard({
  hit,
  navigate,
  query
}: {
  hit: GlobalHit;
  navigate: ReturnType<typeof useNavigate>;
  query: string;
}) {
  const content = getGlobalHitListItemContent(hit, query, {
    personVisibleAttributes: ["pga_id", "email", "section_name", "facility_name"]
  });
  if (!content) return null;

  switch (hit.object_type) {
    case "Person": {
      const personHit = hit as PersonHit;
      return (
        <ListItem
          compact
          useAvatar
          picture={content.picture}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Person"
          tagType="secondary"
          tagStyle="standard"
          tagPosition="trailing"
          longContent
          action={() => {
            const id = personHit.pga_id || personHit.legacy_id;
            if (id) navigate(`/people/${id}` as Path);
          }}
        />
      );
    }
    case "Facility": {
      const facilityHit = hit as FacilityHit;
      return (
        <ListItem
          compact
          useAvatar
          iconName={content.iconName}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Facility"
          tagType="tertiary"
          tagStyle="standard"
          tagPosition="trailing"
          longContent={content.longContent}
          action={() => {
            const id = facilityHit.facility_id;
            if (id) navigate(`/facilities/${id}` as Path);
          }}
        />
      );
    }
    case "Section": {
      const sectionHit = hit as SectionHit;
      return (
        <ListItem
          compact
          useAvatar
          iconName={content.iconName}
          headline={content.headline}
          supportingText={content.supportingText}
          tagText="Section"
          tagType="tertiary"
          tagStyle="standard"
          tagPosition="trailing"
          action={() => {
            const id = sectionHit.section_id;
            if (id) navigate(`/sections/${id}` as Path);
          }}
        />
      );
    }
    default:
      return null;
  }
}

function algoliaFilters(filter: ObjectTypeFilter): string {
  return filter === "all"
    ? "object_type:Person OR object_type:Facility OR object_type:Section"
    : `object_type:${filter}`;
}

export default function GlobalSearchResults() {
  const [searchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const navigate = useNavigate();

  const [results, setResults] = useState<GlobalHit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filter, setFilter] = useState<ObjectTypeFilter>("all");
  const [page, setPage] = useState(0);
  const [nbPages, setNbPages] = useState(0);
  const [nbHits, setNbHits] = useState(0);
  const [typeCounts, setTypeCounts] = useState<TypeCounts>({ Person: 0, Facility: 0, Section: 0 });

  usePageTitle("/search", "Search Results");

  const searchClient = useMemo(() => algoliasearch(appId, apiKey), []);
  const index = useMemo(
    () => searchClient.initIndex(import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME),
    [searchClient]
  );

  const baseSearchConfig = useMemo(() => {
    const uiState = createGlobalSearchUiState(import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME);
    const { filters: _filters, ...rest } =
      uiState[import.meta.env.VITE_GLOBAL_PEOPLE_SEARCH_INDEX_NAME]?.configure ?? {};
    return rest;
  }, []);

  // Reset page and filter when the query changes
  useEffect(() => {
    setPage(0);
    setFilter("all");
  }, [query]);

  // Fetch per-type total counts once per query (hitsPerPage: 0 means no hits, just nbHits)
  useEffect(() => {
    if (!query.trim()) {
      setTypeCounts({ Person: 0, Facility: 0, Section: 0 });
      return;
    }

    const searchQuery = normalizePhoneSearchQuery(query);
    const countParams = { ...baseSearchConfig, hitsPerPage: 0 };

    Promise.all([
      index.search(searchQuery, { ...countParams, filters: "object_type:Person" }),
      index.search(searchQuery, { ...countParams, filters: "object_type:Facility" }),
      index.search(searchQuery, { ...countParams, filters: "object_type:Section" })
    ])
      .then(([people, facilities, sections]) => {
        setTypeCounts({
          Person: people.nbHits,
          Facility: facilities.nbHits,
          Section: sections.nbHits
        });
      })
      .catch(() => {});
  }, [query, index, baseSearchConfig]);

  // Fetch paginated results whenever query, filter, or page changes
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setNbPages(0);
      setNbHits(0);
      return;
    }

    setIsLoading(true);

    collectFilteredHits<GlobalHit>(index, normalizePhoneSearchQuery(query), {
      searchParams: {
        ...baseSearchConfig,
        filters: algoliaFilters(filter)
      },
      filterHits: filterGlobalSearchHits,
      limit: HITS_PER_PAGE,
      offset: page * HITS_PER_PAGE
    })
      .then(({ hits, filteredTotal, hasMore, exhausted }) => {
        // Never strand the user on an empty page past 0 with no Previous control.
        if (hits.length === 0 && page > 0) {
          setPage(page - 1);
          return;
        }

        setResults(hits);

        if (hits.length === 0 && !hasMore) {
          const total = exhausted && filteredTotal !== null ? filteredTotal : 0;
          setNbHits(total);
          setNbPages(0);
          return;
        }

        if (exhausted && filteredTotal !== null) {
          setNbHits(filteredTotal);
          setNbPages(filteredTotal === 0 ? 0 : Math.ceil(filteredTotal / HITS_PER_PAGE));
          return;
        }

        const knownThroughPage = page * HITS_PER_PAGE + hits.length;
        setNbHits(hasMore ? knownThroughPage + 1 : knownThroughPage);
        setNbPages(hasMore ? page + 2 : Math.max(page + 1, 1));
      })
      .catch(() => {
        setResults([]);
        setNbPages(0);
        setNbHits(0);
      })
      .finally(() => setIsLoading(false));
  }, [query, filter, page, index, baseSearchConfig]);

  function changeFilter(next: ObjectTypeFilter) {
    setFilter(next);
    setPage(0);
  }

  const selectedTypeCount =
    filter === "all" ? typeCounts.Person + typeCounts.Facility + typeCounts.Section : typeCounts[filter];
  // Chip/summary counts stay on unfiltered Algolia totals; pagination uses filtered nbHits/nbPages only.
  const totalForFilter = selectedTypeCount > 0 ? selectedTypeCount : nbHits;

  const pageStart = nbHits === 0 || results.length === 0 ? 0 : page * HITS_PER_PAGE + 1;
  const pageEnd = Math.min(pageStart + results.length - 1, nbHits);

  return (
    <AppLayout interior>
      <PageHeading title="Search Results" />

      <p className="mt-2 text-body-large text-sys-brand-on-surface-variant">
        {isLoading
          ? "Searching…"
          : query
            ? `${totalForFilter.toLocaleString()} result${totalForFilter !== 1 ? "s" : ""} for "${query}"`
            : "Enter a search term to see results"}
      </p>

      {(typeCounts.Person + typeCounts.Facility + typeCounts.Section > 0 || isLoading) && (
        <div className="mt-4 mb-6">
          <ChipSet>
            <Chip
              label={`All (${(typeCounts.Person + typeCounts.Facility + typeCounts.Section).toLocaleString()})`}
              selected={filter === "all"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("all")}
            />
            <Chip
              label={`People (${typeCounts.Person.toLocaleString()})`}
              selected={filter === "Person"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Person")}
            />
            <Chip
              label={`Facilities (${typeCounts.Facility.toLocaleString()})`}
              selected={filter === "Facility"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Facility")}
            />
            <Chip
              label={`Sections (${typeCounts.Section.toLocaleString()})`}
              selected={filter === "Section"}
              color="strong"
              showCheckmark={false}
              onClick={() => changeFilter("Section")}
            />
          </ChipSet>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-sys-brand-on-surface-variant">Searching…</div>
      ) : results.length === 0 ? (
        <EmptyState
          iconName="search_off"
          title={
            !query
              ? "Enter a Search Term Above"
              : filter !== "all"
                ? `No ${
                    filter === "Person" ? "People" : filter === "Facility" ? "Facilities" : "Sections"
                  } Matched "${query}"`
                : `No Results Found for "${query}"`
          }
        />
      ) : (
        <>
          <div>
            {results.map((hit, i) => (
              <div key={`${hit.objectID}-${i}`}>
                {i > 0 && <Divider subtle />}
                <div className="py-3">
                  <SearchResultCard hit={hit} navigate={navigate} query={query} />
                </div>
              </div>
            ))}
          </div>

          {nbPages > 1 && (
            <div className="flex items-center justify-end gap-4 mt-6 pb-4">
              <span className="text-label-large text-sys-brand-on-surface-variant">
                {pageStart}–{pageEnd} of {nbHits.toLocaleString()}
              </span>
              <div className="flex items-center gap-2">
                <IconButton
                  name="navigate_before"
                  variant="square"
                  onClick={() => setPage((p) => p - 1)}
                  disabled={page === 0}
                  data-testid="previous-page-button"
                />
                <IconButton
                  name="navigate_next"
                  variant="square"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= nbPages - 1}
                  data-testid="next-page-button"
                />
              </div>
            </div>
          )}
        </>
      )}
    </AppLayout>
  );
}
